import { FaUser } from 'react-icons/fa';
import { MdOutlinePeopleOutline } from 'react-icons/md';

import { getMemberGenderSymbol, isMemberSelectionAllowed } from '@healthquote/utils';
import { HealthMember, MemberSpecificAddons } from '@store/healthquote';
import { Group, Text, Badge, Stack } from '@ui';

import classes from './styles/Blocks.module.css';

// Quote store has addons listed in `member.addons` for selected members
const quoteMembersFilter = (members: HealthMember[], addOnKey: string) =>
  members.filter((member) => {
    const memberAddons = member.addons || [];
    const isSelected =
      memberAddons.find((addon) => addon.key === addOnKey)?.included ?? false;
    return isSelected;
  });

// BE saves addons as `member.addonKey: true` which is data source in status page
const statusMembersFilter = (members: HealthMember[], addOnKey: string) =>
  members.filter(
    (member) => member && member[addOnKey as MemberSpecificAddons] === true
  );

const AddOnMembers = ({
  members,
  productName,
  addOnKey,
  asPills = false,
  fromStatusPage = false
}: {
  members: HealthMember[];
  productName: string;
  addOnKey: string;
  asPills?: boolean;
  fromStatusPage?: boolean;
}) => {
  if (!isMemberSelectionAllowed(productName, addOnKey)) return null;

  const addOnMembers = fromStatusPage
    ? statusMembersFilter(members, addOnKey)
    : quoteMembersFilter(members, addOnKey);

  if (!addOnMembers?.length) return null;

  if (asPills) {
    return (
      <Stack gap="sm">
        <Text fz={12} fw={500}>
          Selected Members:
        </Text>
        <Group gap="xs">
          {addOnMembers.map((member, index) => (
            <Badge
              key={`${member.relation}-${index}`}
              size="xl"
              radius="xs"
              h={24}
              fz={11}
              leftSection={<FaUser color="#B6B8BA" size={12} />}
              classNames={{
                root: classes.addOnMemberBadge,
                label: classes.addOnMemberBadgeLabel
              }}
            >
              {member.relation}
              {member.gender ? ` (${getMemberGenderSymbol(member.gender)}) ` : ' '}(
              {member.age})
            </Badge>
          ))}
        </Group>
      </Stack>
    );
  }

  const membersWithRelationAndAge = addOnMembers?.map((member) => {
    return `${member.relation}${
      member.gender ? `(${getMemberGenderSymbol(member.gender)})` : ''
    }(${member.age})`;
  });

  return (
    <Group
      align="center"
      gap={4}
      className={classes.addOnMemberBadgeLabel}
      mt="xs"
      ml="lg"
    >
      <MdOutlinePeopleOutline size={11} />
      <Text fz={11}>{membersWithRelationAndAge.join(', ')}</Text>
    </Group>
  );
};

export { AddOnMembers };
